import { Box, Button, CircularProgress, IconButton } from '@mui/material';
import { Edit, Upload } from '@mui/icons-material';
import { useRef, useState } from 'react';
import {
    confirmLogoUpload,
    getPresignedLogoUrl
} from '../../../api-admin/tenants';
import { s3Upload } from '../../../api-admin/s3-upload';
import toastMessage from '../../../utils/toast';
import Logotype from './Logotype';

type PropsType = {
    tenantId: number | string;
    url?: string | null;
    name: string;
    size: number;
    showButton?: boolean;
    onUploaded?: () => void | Promise<void>;
};

export default function LogotypeHandler({
    tenantId,
    url,
    name,
    size,
    showButton = false,
    onUploaded
}: PropsType) {
    const [isUploading, setIsUploading] = useState(false);
    const hiddenFileInput = useRef<HTMLInputElement>(null);

    const handleClick = () => {
        if (isUploading) return;
        hiddenFileInput.current?.click();
    };

    const handleImgUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file) {
            toastMessage('Ingen fil vald');
            return;
        }

        const fileSize = file.size;
        const mimeType = file.type;

        if (!mimeType) {
            toastMessage('Kunde inte läsa filtypen. Använd PNG, JPG eller SVG.');
            return;
        }

        if (fileSize / 1024 / 1024 > 10) {
            toastMessage('Filen måste vara mindre än 10MB');
            return;
        }

        setIsUploading(true);
        try {
            const { presignedUrl } = await getPresignedLogoUrl(tenantId, { mimeType });
            await s3Upload({ file, presignedUrl, mimeType });
            await confirmLogoUpload(tenantId, { mimeType });
            await onUploaded?.();
            toastMessage('Logotypen har uppdaterats');
        } catch (error) {
            console.error(error);
            toastMessage('Ett fel uppstod vid uppladdningen, försök igen senare');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <>
            <Box sx={{
                position: 'relative',
                display: 'inline-flex'
            }}
            >
                <Logotype url={url} name={name} size={size} />

                <Box
                    onClick={handleClick}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isUploading ? 'default' : 'pointer',
                        pointerEvents: isUploading ? 'none' : 'auto',
                        bgcolor: 'transparent',
                        transition: 'background-color 0.2s',
                        '&:hover': isUploading
                            ? {}
                            : { bgcolor: 'rgba(0, 0, 0, 0.5)' },
                        '&:hover .edit-icon': isUploading
                            ? {}
                            : { opacity: 1 },
                    }}
                >
                    <IconButton
                        className="edit-icon"
                        sx={{
                            opacity: 0,
                            color: 'common.white',
                            transition: 'opacity 0.2s',
                            fontSize: size * 0.35
                        }}
                    >
                        <Edit />
                    </IconButton>
                </Box>

                {isUploading && (
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                    }}
                    >
                        <CircularProgress size={size * 0.35} sx={{ color: 'common.white' }} />
                    </Box>
                )}

                {!isUploading && (
                    <input
                        type="file"
                        accept="image/png, image/jpeg, image/svg+xml"
                        ref={hiddenFileInput}
                        style={{ display: 'none' }}
                        onChange={handleImgUpload}
                    />
                )}
            </Box>
            {showButton && (
                <Button
                    onClick={handleClick}
                    disabled={isUploading}
                    variant="contained"
                    sx={{
                        marginTop: 2,
                        width: '100%',
                    }}
                >
                    <Upload fontSize="small" />
                    {url ? ' Ladda upp ny logotyp' : ' Ladda upp logotyp'}
                </Button>
            )}
        </>
    );
}
